/**
 * Fetch middleware
 *
 * Middleware which handles going to the server for data
 */

import { ajax, jsonp } from 'jquery';
import { getErrorFromXHR, getMergedError, globalErrorHandler } from './errorHandling';
import { pluraliseAndTranslate } from './globalisation';
import { getLogger } from 'loglevel';
import config from '../js/config';
import cookies from 'js-cookie';
import '../js/third-party/jquery.jsonp';
import utilities from '../js/utilities';
const log = getLogger('makeRequest');

export function jQueryRequestFetchMiddleware(request = {}) {
  return new Promise((resolve, reject) => {
    if (request.response) {
      log.trace(`Existing response, not going to server for ${request.requestOptions.url}`);
      resolve(request);
    } else {
      log.trace(`No cache, going to server for ${request.requestOptions.url}`);
      const ajaxOptions = {
        timeout: 20000,
        ...request.requestOptions
      };
      ajax(ajaxOptions)
        .done((response, textStatus, xhr) => {
          request.statusCode = xhr.status;
          request.response = response ? response.data || response || {} : {};
          resolve(request);
        })
        .fail((xhr, textStatus, errorThrown) => {
          let err = new Error(errorThrown);
          err.request = request;
          request.statusCode = xhr.status;
          request.error = getErrorFromXHR(xhr);
          reject(err);
        });
    }
  });
}

/**
 * JSONP
 * =====
 * Fetch data using jsonp
 *
 * This uses the jsonp plugin from here:
 * https://github.com/jaubourg/jquery-jsonp/blob/master/doc/API.md
 * @param request
 * @return {Promise}
 */
export function jsonpRequestFetchMiddleware(request = {}) {
  return new Promise((resolve, reject) => {
    if (request.response) {
      log.trace(`Existing response, not going to server for ${request.requestOptions.url}`);
      resolve(request);
    } else {
      log.trace(`No cache, going to server for ${request.requestOptions.url}`);
      jsonp({
        callbackParameter: 'callback',
        timeout: 10000,
        ...request.requestOptions,
        success: function(json, textStatus, $xhr) {
          request.statusCode = $xhr.status;
          request.response = json;
          resolve(request);
        },
        error: function(xOptions, textStatus) {
          let err = new Error(textStatus);
          err.request = request;
          request.error = getMergedError({
            type: xOptions && xOptions.state ? xOptions.state() : null
          });
          switch (textStatus) {
            case 'timeout':
              request.statusCode = xOptions.status || 0;
              request.error.type = config.error.type.timeout;
              request.error.title = pluraliseAndTranslate('common', 'genericTimeoutErrorTitle');
              request.error.messages.push({
                message: pluraliseAndTranslate('common', 'genericTimeoutErrorMessage')
              });
              break;
            default:
              request.statusCode = xOptions.status || 400;
          }
          reject(err);
        }
      });
    }
  });
}

export function hybrisRequestFetchMiddleware(request = {}) {
  if (request.requestOptions.hybrisApi) {
    addAccessToken(request.requestOptions);
  }
  return jQueryRequestFetchMiddleware(request)
    .then((request) => {
      if (request.requestOptions && !request.requestOptions.handleErrorLocally) {
        const errors = request.response.errors;
        if (errors && errors.length) {
          globalErrorHandler(errors[0])
        }
      }
      return Promise.resolve(request);
    })
    .catch((err) => {
      return new Promise((resolve, reject) => {
        const req = err.request;
        let url = ''
        if (req && req.requestOptions && req.requestOptions.url) {
          url = req.requestOptions.url;
        }
        const arrowFed = cookies.get(config.cookies.arrowFed);
        if (url.indexOf(config.hybris.baseUrl) === 0) {
          if ((url !== config.hybris.tokenUrl) && (req.statusCode === 401)) {
            if (utilities.isEmptyArrowfed(arrowFed)) {  // hybris call with invalid arrowfed
              redirectTologin();
              reject(request);
            } else { // hybris call with valid arrowfed
              return refreshToken()
                .then(() => {
                  request.options = addAccessToken(request.requestOptions);
                  return jQueryRequestFetchMiddleware(request);
                }, (tokenError) => {
                  if ((tokenError.status === 400) || (tokenError.status === 401)) {
                    redirectTologin();
                  }
                  reject(tokenError);
                }).then(
                  (response2) => {
                    resolve(response2);
                  },
                  (error2) => {
                    reject(error2);
                  })
            }
          } else {
            reject(request);
          }
        } else {
          reject(request);
        }
      })
    });
}


/**
 * refresh or obtain a token using standard promise
 */
export function refreshToken() {
  return new Promise((resolve, reject) => {
    return jQueryRefreshToken().then(
      (response) => {
        resolve(response);
      },
      (err) => {
        reject(err);
      }
    )
  });
}

/**
 * refresh or obtain a token
 */
export function jQueryRefreshToken() {
  const arrowFed = cookies.get(config.cookies.arrowFed);
  return getToken(arrowFed)
    .then((tokenResponse) => {
      cookies.set(
        config.cookies.accessToken,
        tokenResponse.access_token,
        {
          domain: config.cookies.domain,
          expires: (0.99 / 24) * 4 // less then 4 hours. arrowfed cookie expire in three hours
        }
      );
      return tokenResponse;
    });
}

function getToken(arrowFed) {
  return ajax({
    url: config.hybris.tokenUrl,
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded'
    },
    'data': {
      arrowfed: arrowFed
    }
  })
  .fail(function() {
    cookies.remove(config.cookies.arrowFed, { domain: config.cookies.domain });
    cookies.remove(config.cookies.accessToken, { domain: config.cookies.domain });
  });
}
function addAccessToken(options = {}) {
  const token = cookies.get(config.cookies.accessToken);
  const arrowFed = cookies.get(config.cookies.arrowFed);
  if (!options.headers) {
    options.headers = {};
  }
  if (arrowFed) {
    options.headers.Authorization = `Bearer ${token}`
  }
  return options;
}

/**
 * redirect to login
 */
function redirectTologin() {
  if (config.urls && config.urls.users) {
    // skip redirect to login page from login page to avoid infinite loop(redirect)
    if (config.urls.users.login) {
      let loginUrl = config.urls.users.login.replace(/[?].*$/, '');
      if (window.location.href.indexOf(loginUrl) > -1) {
        return;
      }
    }
    // skip redirect to login page from register page
    if (config.urls.users.register) {
      if (window.location.href.indexOf(config.urls.users.register) > -1) {
        return;
      }
    }
  }
  cookies.remove(config.cookies.arrowFed, { domain: config.cookies.domain });
  cookies.remove(config.cookies.accessToken, { domain: config.cookies.domain });
  window.location = config.urls.users.login.replace('{{url}}', encodeURIComponent(window.location.href));
}



// WEBPACK FOOTER //
// ./assets/utilities/requestFetchMiddleware.js